import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { LogoMark } from "@/components/site/Logo";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const AdminLogin = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin/leads");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-sand">
      <div className="hidden lg:block relative grain">
        <img src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=compress&w=1400&q=80" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-forest/70" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-sand">
          <Link to="/" className="flex items-center gap-2.5">
            <span className="grid place-items-center w-9 h-9 rounded-full bg-sand text-forest"><LogoMark className="w-5 h-5" /></span>
            <span className="font-serif text-xl">New Beginnings Recovery</span>
          </Link>
          <p className="font-serif text-4xl leading-tight max-w-md">Staff portal — manage enquiries and resources with care.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-md">
          <div className="grid place-items-center w-12 h-12 rounded-full bg-forest text-sand mb-6"><Lock className="w-5 h-5" /></div>
          <h1 className="font-serif text-4xl text-forest">Welcome back</h1>
          <p className="mt-2 text-ink2">Sign in to the admissions dashboard.</p>

          {error && <p data-testid="admin-login-error" className="mt-6 rounded-xl bg-destructive/10 text-destructive px-4 py-3 text-sm">{error}</p>}

          <div className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-ink">Email</Label>
              <Input id="email" data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 rounded-xl border-line bg-white" placeholder="admin@newbeginningsrecovery.ca" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-ink">Password</Label>
              <Input id="password" data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 rounded-xl border-line bg-white" placeholder="••••••••" />
            </div>
          </div>

          <Button type="submit" data-testid="admin-login-submit" disabled={loading} className="mt-8 w-full h-13 py-6 rounded-full bg-forest hover:bg-forestdark text-sand font-semibold">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign in"}
          </Button>
          <Link to="/" className="mt-6 block text-center text-sm text-ink2 hover:text-forest">← Back to website</Link>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;
